import dayjs from 'dayjs';
import { nfRp } from '@/constants/nf';
import { getDiscountItem } from '@/store/slices/cartSlice';

/** Stable empty list for selectors (avoids new [] each render). */
export const EMPTY_CART_ITEMS = Object.freeze([]);

/** Normalize meal / cart line date to YYYY-MM-DD. */
export function mealDateIso(value) {
	if (value == null || value === '') return '';
	return dayjs(value).format('YYYY-MM-DD');
}

/** My cart badge: sum of portions, not line count. */
export function cartQuantityTotal(items) {
	return (items || []).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
}

export function linesForChild(items, customerId) {
	if (customerId == null || customerId === '') return [];
	const id = Number(customerId);
	return (items || []).filter((item) => Number(item.customerId) === id);
}

export function linesForChildAndDate(items, customerId, dateIso) {
	const dateKey = mealDateIso(dateIso);
	return linesForChild(items, customerId).filter((item) => mealDateIso(item.date) === dateKey);
}

/** Distinct child ids that have unpaid cart lines. */
export function cartOwnerIds(items) {
	const ids = new Set();
	(items || []).forEach((item) => {
		if (item.customerId != null && item.customerId !== '') {
			ids.add(Number(item.customerId));
		}
	});
	return [...ids];
}

/** Meal dates (YYYY-MM-DD) that have at least one unpaid cart line. */
export function cartMealDates(items) {
	const dates = new Set();
	(items || []).forEach((item) => {
		const d = mealDateIso(item.date);
		if (d && d !== 'Invalid Date') dates.add(d);
	});
	return dates;
}

/** Distinct meal dates with unpaid lines (family cart). */
export function cartMealDateCount(items) {
	return cartMealDates(items).size;
}

export function childInitials(name) {
	return (name || '')
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((p) => p[0]?.toUpperCase())
		.join('');
}

/** Unit price + add-ons for one cart / unpaid preview line. */
export function cartLineUnitWithAddOns(line) {
	const unit =
		('unit_price' in line
			? +line.unit_price
			: 'price' in line
				? +line.price
				: +line.selling_price) || 0;
	const addOns = (line.add_ons || []).reduce(
		(a, b) => a + (+b.add_on?.price || +b?.price || 0),
		0
	);
	return unit + addOns;
}

/** Gross line total (no discount). */
export function cartLineGrossTotal(line) {
	const qty = Number(line?.quantity) || 0;
	return cartLineUnitWithAddOns(line) * qty;
}

/** Discount amount for one unpaid cart line (0 when none). */
export function cartLineDiscountAmount(line) {
	if (!line?.discount) return 0;
	const qty = Number(line.quantity) || 0;
	return getDiscountItem(cartLineUnitWithAddOns(line), qty, line.discount) || 0;
}

/** Net line total after inline item discount. */
export function cartLineNetTotal(line) {
	return Math.max(0, cartLineGrossTotal(line) - cartLineDiscountAmount(line));
}

/**
 * Short note under a meal row, same tone as Cart: `disc (RpX)` / `disc (RpX) — name`.
 * Returns '' when amount is 0.
 */
export function formatLineDiscountNote(amount, name) {
	const n = Number(amount) || 0;
	if (n <= 0) return '';
	const base = `disc (${nfRp(n)})`;
	const label = typeof name === 'string' ? name.trim() : '';
	return label ? `${base} — ${label}` : base;
}

/** Cart / Orders style add-on label. */
export function formatAddOnLineLabel(addOn) {
	const name = addOn?.add_on?.name || addOn?.name || 'Add-on';
	const price = Number(addOn?.add_on?.price ?? addOn?.price ?? 0) || 0;
	return price === 0 ? `${name} · Free` : `${name} · ${nfRp(price)}`;
}

/** Cart / Orders style modifier label (modifiers are free). */
export function formatModifierLineLabel(mod) {
	const name = mod?.modifier?.name || mod?.name || 'Modifier';
	return `${name} · Free`;
}

/** Prefer cart `notes`, then order/dashboard `note`. */
export function mealLineNoteText(line) {
	const raw = line?.notes ?? line?.note ?? '';
	return typeof raw === 'string' ? raw.trim() : '';
}

/** Gross line total minus any inline item discount (Fixed Price / Amount / %). */
export function unpaidTotalForLines(lines) {
	return (lines || []).reduce((sum, item) => sum + cartLineNetTotal(item), 0);
}
